import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { useCreateRole } from '../../services/roles';

const CreateRoleModal = ({ show, onHide, onSuccess }) => {
  const queryClient = useQueryClient();

  const [roleName, setRoleName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [roleGroup, setRoleGroup] = useState('internal');
  const [errors, setErrors] = useState({});

  const createRole = useCreateRole(
    (data) => {
      toast.success('Role created');
      queryClient.invalidateQueries(['roles']);
      if (onSuccess) onSuccess();
      handleClose();
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.msg || 'Failed to create role');
      },
    }
  );

  const handleClose = () => {
    setRoleName('');
    setDisplayName('');
    setDescription('');
    setRoleGroup('internal');
    setErrors({});
    onHide();
  };

  const handleRoleNameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setRoleName(value);
    if (value.length < 3 || value.length > 50) {
      setErrors((prev) => ({ ...prev, roleName: 'Role name must be 3-50 characters' }));
    } else {
      setErrors((prev) => ({ ...prev, roleName: null }));
    }
  };

  const handleDisplayNameChange = (e) => {
    setDisplayName(e.target.value);
    if (e.target.value.trim().length === 0) {
      setErrors((prev) => ({ ...prev, displayName: 'Display name is required' }));
    } else {
      setErrors((prev) => ({ ...prev, displayName: null }));
    }
  };

  const isValid =
    roleName.trim().length >= 3 &&
    roleName.trim().length <= 50 &&
    displayName.trim().length > 0 &&
    !errors.roleName &&
    !errors.displayName;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;

    createRole.mutate({
      roleName: roleName.trim(),
      displayName: displayName.trim(),
      description: description.trim() || null,
      roleGroup: roleGroup,
      permissions: [],
    });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Role</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="text-sm font-medium text-slate-700">
              Role Name <span className="text-red-500">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={roleName}
              onChange={handleRoleNameChange}
              placeholder="e.g., claims_admin"
              isInvalid={!!errors.roleName}
              autoFocus
            />
            <Form.Text className="text-xs text-slate-500">
              Lowercase letters, numbers, and underscores only. 3-50 characters.
            </Form.Text>
            {errors.roleName && (
              <Form.Control.Feedback type="invalid">
                {errors.roleName}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-sm font-medium text-slate-700">
              Display Name <span className="text-red-500">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={displayName}
              onChange={handleDisplayNameChange}
              placeholder="e.g., Claims Administrator"
              isInvalid={!!errors.displayName}
            />
            {errors.displayName && (
              <Form.Control.Feedback type="invalid">
                {errors.displayName}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-sm font-medium text-slate-700">
              Role Group <span className="text-red-500">*</span>
            </Form.Label>
            <Form.Select
              value={roleGroup}
              onChange={(e) => setRoleGroup(e.target.value)}
            >
              <option value="internal">Internal</option>
              <option value="provider">Provider</option>
              <option value="client">Client</option>
            </Form.Select>
            <Form.Text className="text-xs text-slate-500">
              Determines which users can be assigned this role.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label className="text-sm font-medium text-slate-700">
              Description
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this role is for..."
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isValid || createRole.isLoading}
          className="inline-flex items-center gap-2"
        >
          {createRole.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateRoleModal;
